interface ClipListCardHistoryInput {
  emittedAttemptId?: string;
  currentAttemptId?: string;
  emittedRenderCount?: number;
  currentRenderCount: number;
  openEditor?: 'clips' | 'script';
  currentVideoStatus?: string | null;
}

/**
 * Whether a chat card describes a cut that can no longer be acted on.
 *
 * Saving a script on a rendered video is the one deliberate exception to the
 * ordinary attempt-id rule: the save forks a draft before the owner chooses
 * narration, and the script card is the surface that must follow that fork to
 * offer the final Accept. The lower render count proves that this is the fresh,
 * unrendered child. Once that child renders, the exception expires.
 */
export function isClipListCardHistorical({
  emittedAttemptId,
  currentAttemptId,
  emittedRenderCount,
  currentRenderCount,
  openEditor,
  currentVideoStatus,
}: ClipListCardHistoryInput): boolean {
  if (emittedAttemptId === undefined || currentAttemptId === undefined) {
    return false;
  }

  const attemptChanged = emittedAttemptId !== currentAttemptId;
  // A render counter can advance and then the same cut can return to draft
  // after a saved edit. That cut is not history: it is precisely the version
  // the owner still needs to Accept. Failed renders are likewise retryable.
  const canActOnCurrentDraft =
    !attemptChanged &&
    (currentVideoStatus === 'draft' || currentVideoStatus === 'failed');
  if (canActOnCurrentDraft) return false;

  const canRetryFailedCut =
    currentVideoStatus === 'failed' &&
    openEditor === 'script' &&
    attemptChanged &&
    emittedRenderCount !== undefined &&
    emittedRenderCount > 0;
  if (canRetryFailedCut) return false;

  const followsScriptSaveFork =
    openEditor === 'script' &&
    attemptChanged &&
    emittedRenderCount !== undefined &&
    emittedRenderCount > 0 &&
    currentRenderCount === 0;

  return (
    (attemptChanged && !followsScriptSaveFork) ||
    (emittedRenderCount !== undefined &&
      currentRenderCount > emittedRenderCount)
  );
}
